import { Component, inject, signal } from "@angular/core";
import { Subject, throttleTime } from "rxjs";

import { GlobalService } from "@bitwarden/common/services/global/global.service";
import { ThemeType } from "@bitwarden/common/enums/themeType";

import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { PreferenceView } from "@bitwarden/web-vault/app/models/view/preference/preference.view";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  AllowedLocations,
  CurrencyType,
  DefaultSystemLocation,
  UserLocationType,
} from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";
import { DropdownSelectMapOption } from "@bitwarden/web-vault/app/directives/dropdown.directive";
import { isOfType } from "@bitwarden/web-vault/app/models/types/general-types";

export type AvailableLocations = Exclude<UserLocationType, "NZ" | "EU" | "JP" | "CN">;

@Component({
  selector: "app-manage-preferences",
  templateUrl: "./manage-preferences.component.html",
})
export class ManagePreferencesComponent {
  private userStoreService: UserStoreService = inject(UserStoreService);
  private roleAccessService: RoleAccessService = inject(RoleAccessService);
  private globalService: GlobalService = inject(GlobalService);
  private buttonSubmitSubject = new Subject<void>();

  protected formState = signal({
    isSaving: false,
  });

  /** Lock down the location dropdown for now (map so realValue = code) **/
  protected userLocationOptions: DropdownSelectMapOption[] = [
    { realValue: "AU", optionDisplayValue: "Australia", selectedDisplayValue: "Australia" },
    { realValue: "HK", optionDisplayValue: "Hong Kong", selectedDisplayValue: "Hong Kong" },
    { realValue: "US", optionDisplayValue: "United States", selectedDisplayValue: "United States" },
  ];

  protected readonly themeOptions: Record<string, ThemeType> = {
    Light: ThemeType.Light,
    Dark: ThemeType.Dark,
    System: ThemeType.System,
  };

  protected readonly dateFormatOptions = ["dd/MM/yyyy"];
  protected currentPreference: PreferenceView;
  protected currencyOptions;
  protected selectedLocation: string;
  protected selectedTheme: string;

  constructor() {
    this.currentPreference = this.userStoreService.preferences.preferenceView().clone();
    this.buttonSubmitSubject
      .asObservable()
      .pipe(throttleTime(200), takeUntilDestroyed())
      .subscribe(this.submit.bind(this));

    this.currencyOptions = this.roleAccessService.getRoleAccess().getClaim().forex_currency;
    // `currentPreference.userLocation` stores the real code (eg. "AU"), so expose that directly
    this.selectedLocation = this.currentPreference.userLocation || DefaultSystemLocation;
    // Go through onThemeSelected so an unset or legacy theme is stored as Light, matching the dropdown
    // (the dropdown no longer emits on init when it is pre-seeded).
    this.onThemeSelected(this.themeDisplayName(this.currentPreference.theme));
  }

  onUserLocationSelected(value: string) {
    if (isOfType<UserLocationType>(value, AllowedLocations)) {
      this.currentPreference.userLocation = value as UserLocationType;
    } else {
      this.currentPreference.userLocation = DefaultSystemLocation;
    }
  }

  onBaseCurrencySelected(value: string) {
    if (isOfType<CurrencyType>(value, this.currencyOptions)) {
      this.currentPreference.coreCurrency = value;
    } else {
      this.currentPreference.coreCurrency = "AUD";
    }
  }

  onDateFormatSelected(event: string) {
    this.currentPreference.dateFormat = event;
  }

  onThemeSelected(displayName: string): void {
    const theme = this.themeOptions[displayName] ?? ThemeType.Light;
    this.currentPreference.theme = theme;
    this.selectedTheme = displayName;
  }

  onSubmitForm() {
    this.formState.set({ ...this.formState(), isSaving: true });
    this.buttonSubmitSubject.next();
  }

  async submit(): Promise<void> {
    try {
      await this.userStoreService.preferences.save(this.currentPreference);
      this.currentPreference = this.userStoreService.preferences.preferenceView().clone();
    } catch (error) {
      this.globalService.showErrorMessage("error", "saveToVaultError");
    }
    this.formState.set({ ...this.formState(), isSaving: false });
  }

  private themeDisplayName(theme: ThemeType): string {
    return (
      Object.keys(this.themeOptions).find((key) => this.themeOptions[key] === theme) ?? "Light"
    );
  }

  protected readonly Object = Object;
}
